/**
 * Start new batch confirmation.
 *
 * The results of a run exist only on this screen. Nothing on the server records
 * what an operator saw, so once the batch is cleared the CSV is the only
 * account of what happened, and there is no way back to it. The checkbox is a
 * deliberate pause before that.
 *
 * It is not a hard gate on having downloaded anything: a batch of one or two
 * rows often does not warrant sending a file at all, and blocking those would
 * teach people to click through the prompt without reading it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Icon, Message, Modal } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import styles from '../BulkChargeGuarantee.module.css';

interface StartNewBatchModalProps {
  isOpen: boolean;
  charityCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const StartNewBatchModal: React.FC<StartNewBatchModalProps> = ({
  isOpen,
  charityCount,
  onConfirm,
  onCancel
}) => {
  const [isAccepted, setIsAccepted] = useState(false);

  // Reset whenever the modal reopens, so a previous tick never carries over.
  useEffect(() => {
    if (isOpen) {
      setIsAccepted(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsAccepted(false);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    if (isAccepted) {
      onConfirm();
    }
  }, [isAccepted, onConfirm]);

  return (
    <Modal open={isOpen} onClose={handleClose} size="small" closeIcon>
      <Modal.Header>
        <Icon name="warning sign" color="orange" />
        <Translate value={i18nKeys.NEW_BATCH.TITLE} />
      </Modal.Header>

      <Modal.Content>
        <Message warning>
          <p>
            <Translate value={i18nKeys.NEW_BATCH.WARNING} />
          </p>
        </Message>

        {charityCount > 1 && (
          <p>
            {I18n.t(i18nKeys.NEW_BATCH.PER_CHARITY_NOTE, {
              count: charityCount
            })}
          </p>
        )}

        <div className={styles.modalCheckbox}>
          <Checkbox
            label={I18n.t(i18nKeys.NEW_BATCH.CHECKBOX_LABEL)}
            checked={isAccepted}
            onChange={(_, data) => setIsAccepted(!!data.checked)}
          />
        </div>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose}>
          <Translate value={i18nKeys.NEW_BATCH.CANCEL_BUTTON} />
        </Button>
        <Button primary onClick={handleConfirm} disabled={!isAccepted}>
          <Translate value={i18nKeys.NEW_BATCH.CONFIRM_BUTTON} />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default StartNewBatchModal;
